/*--external-- */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import classNames from "classnames";

/**--internal-- */
import { graphModes } from "Worksheet/modules/WorksheetConstants";

/**--relative--*/
import classes from "./LabellingField.scss";
import Labelling from "./Labelling";
import { LABELLING_CATEGORY } from "./Labelling/LabellingConstant";
import {
  convertDataToPercentage,
  convertDataToPixel,
  extractCorrectElementsFromData,
  getLabelPlaceholder,
} from "./Labelling/LabellingUtils";

/**
 *
 * @param { mode, t }
 * @returns DragAndDropField Header
 */
const LabellingFieldHeader = ({ mode, t }) => {
  if (mode !== "create") return null;

  const headerSubTextClasses = classNames(
    classes.headerSubTextContainer,
    "text-body-2 text-textSubtle"
  );

  return (
    <div className={classes.headerContainer}>
      <div className={classes.leftContainer}>
        <div className="text-label-2">{t("worksheet:labelling_tool")}</div>
        <div className={headerSubTextClasses}>
          {t("worksheet:labelling_question_subtext")}
        </div>
      </div>
    </div>
  );
};

/**
 *
 * @param {*} props
 * @returns Labelling Question type Field
 */
const LabellingField = props => {
  const {
    value = {},
    t = () => "",
    mode = "preview",
    fieldId = "",
    questionId = "",
    resourceId = "",
    questionIndex = 0,
    customRef = () => {},
    isSubmitted = false,
    showSolution = false,
    isSubQuestion = false,
    fieldKey = "labelling",
    allowInteraction = false,
    subQuestionParentId = "",
    updateInputField = () => {},
    showViewSolutionButton = false,
    worksheetMode,
    isFromInternalTool = false,
  } = props;

  const componentRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [questionData, setQuestionData] = useState([]);
  const [canvasDimension, setCanvasDimension] = useState({});
  const [viewSolution, setViewSolution] = useState(false);

  const config = {
    fieldId,
    fieldKey,
    questionId,
    resourceId,
    isSubmitted,
    showSolution,
    questionIndex,
    isSubQuestion,
    allowInteraction,
    subQuestionParentId,
    showViewSolutionButton,
  };

  // * in draft mode, showing correct answer in MODE_ASSESSMENT_VIEW
  useEffect(() => {
    if (mode === "solution_view" || mode === "view") {
      setViewSolution(mode === "solution_view");
    }
  }, [mode]);

  // * Observing canvas resize in width/height and sending callback
  useEffect(() => {
    let resizeObserver;
    const observeCanvasResize = () => {
      const canvasId = `konva-canvas-container-id-${fieldId}-${mode}`;
      const targetElement = document.getElementById(canvasId);
      if (!targetElement) return;

      resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          setCanvasDimension({ width, height });
        }
      });

      resizeObserver.observe(targetElement);
    };

    // * adding resize observer and disconnect cleanup of observer
    const observerTimeout = setTimeout(observeCanvasResize, 100);
    return () => {
      clearTimeout(observerTimeout);
      if (resizeObserver) resizeObserver.disconnect();
      resizeObserver = null;
    };
  }, [mode, fieldId, JSON.stringify(canvasDimension)]);

  // * Converting percentage data into pixel according to canvas width
  useEffect(() => {
    try {
      convertDataToPixel(
        value,
        fieldId,
        mode,
        setQuestionData,
        t,
        viewSolution,
        worksheetMode
      );
    } catch (error) {}
  }, [
    mode,
    fieldId,
    isFullscreen,
    viewSolution,
    canvasDimension,
    mode === "preview" && _.isObject(value) ? JSON.stringify(value) : value,
  ]);

  // * Setting custom ref for worksheet wrapper
  useEffect(() => {
    customRef(componentRef.current);
  }, [customRef]);

  // * Updating local data and saving on server
  const handleDataUpdate = updatedData => {
    try {
      setQuestionData(updatedData);

      if (!_.includes(["create", "edit"], mode)) return;
      const isCreateMode = mode === "create";

      const dataInPercentage = convertDataToPercentage(
        updatedData,
        fieldId,
        mode
      );

      const { elements } = extractCorrectElementsFromData(
        mode,
        dataInPercentage
      );

      /**
       * for label category element
       * if empty text, checking isPlaceholder and updating empty text
       * if text, for create mode saving in ; splited array else trimed text
       */
      const labellingAnswer = {};
      _.forEach(elements, element => {
        if (_.get(element, "category") === LABELLING_CATEGORY.LABEL) {
          let text = _.get(element, "text", "");
          const isPlaceholder =
            _.isEmpty(text) ||
            text === getLabelPlaceholder(t, "create") ||
            text === getLabelPlaceholder(t, "edit");

          if (isPlaceholder) {
            text = isCreateMode ? [] : "";
          } else {
            text = isCreateMode ? _.split(text, ";") : text;
          }
          _.set(labellingAnswer, element.id, text);
          _.unset(element, "text");
        }
      });

      const params = isCreateMode
        ? { value: { elements }, labellingAnswer }
        : { value: { elements, answers: labellingAnswer } };

      updateInputField?.({
        params,
        isMutation: true,
        isDebounce: true,
      });
    } catch (error) {}
  };

  return (
    <div ref={componentRef} className={classes.container}>
      <LabellingFieldHeader t={t} mode={mode} />
      <Labelling
        t={t}
        mode={mode}
        key={fieldId}
        config={config}
        data={questionData}
        isFullscreen={isFullscreen}
        viewSolution={viewSolution}
        setViewSolution={setViewSolution}
        onUpdateData={handleDataUpdate}
        onToggleFullscreen={() => setIsFullscreen(prev => !prev)}
        worksheetMode={worksheetMode}
        isFromInternalTool={isFromInternalTool}
      />
    </div>
  );
};

export default LabellingField;

LabellingField.displayName = "LabellingField";

LabellingField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  mode: PropTypes.oneOf(graphModes),
  updateInputField: PropTypes.func,
  fieldKey: PropTypes.string,
  questionIndex: PropTypes.number,
  isSubQuestion: PropTypes.bool,
  isSubmitted: PropTypes.bool,
  resourceId: PropTypes.string,
  questionId: PropTypes.string,
  fieldId: PropTypes.string,
  subQuestionParentId: PropTypes.string,
};
